import React, { useState, useEffect, useRef } from "react";
import { useSubmission } from "../../../hooks/useCallApi";
import { complaintsAPI } from "../../../services/api/admin/complaintsApi";
import FormInput from "../../common/FormInput";

const AssignmentActionsTab = ({ 
  register, 
  errors, 
  setValue, 
  watch, 
  searchTerms,
  setSearchTerms,
  complaintData 
}) => {
  const [assignedToResults, setAssignedToResults] = useState([]);
  const [showAssignedToResults, setShowAssignedToResults] = useState(false);
  const [assignedByResults, setAssignedByResults] = useState([]);
  const [showAssignedByResults, setShowAssignedByResults] = useState(false);
  const [actionByResults, setActionByResults] = useState([]);
  const [showActionByResults, setShowActionByResults] = useState(false);

  const assignedToRef = useRef(null);
  const assignedToInputRef = useRef(null);
  const assignedByRef = useRef(null);
  const assignedByInputRef = useRef(null);
  const actionByRef = useRef(null);
  const actionByInputRef = useRef(null);

  const [activeSearchField, setActiveSearchField] = useState(null);

  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const searchUserMutation = useSubmission(
    ["searchUsers"],
    (api, searchParams) => complaintsAPI.searchAssigned(searchParams),
    {
      onSuccess: (res) => {
        console.log("========== SEARCH RESPONSE RECEIVED ==========");
        console.log("Full response object:", res);
        
        let userData = [];
        
        if (res?.data?.data?.searched_users && Array.isArray(res.data.data.searched_users)) {
          userData = res.data.data.searched_users;
        } else if (res?.data?.searched_users && Array.isArray(res.data.searched_users)) {
          userData = res.data.searched_users;
        } else if (res?.searched_users && Array.isArray(res.searched_users)) {
          userData = res.searched_users;
        } else {
          console.log("❌ No user data array found in response");
        }

        console.log("Final processed user data:", userData);
        console.log("Active search field:", activeSearchField);
        
        if (activeSearchField === 'assignedTo') {
          setAssignedToResults(userData);
          setShowAssignedToResults(true);
        } else if (activeSearchField === 'assignedBy') {
          setAssignedByResults(userData);
          setShowAssignedByResults(true);
        } else if (activeSearchField === 'actionBy') {
          setActionByResults(userData);
          setShowActionByResults(true);
        }
      },
      onError: (error) => {
        console.error("========== SEARCH ERROR ==========");
        console.error("Error details:", error);
        
        if (activeSearchField === 'assignedTo') {
          setAssignedToResults([]);
        } else if (activeSearchField === 'assignedBy') {
          setAssignedByResults([]);
        } else if (activeSearchField === 'actionBy') {
          setActionByResults([]);
        }
      },
    }
  );

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (assignedToRef.current && !assignedToRef.current.contains(event.target)) {
        setShowAssignedToResults(false);
        setActiveSearchField(null);
      }
      if (assignedByRef.current && !assignedByRef.current.contains(event.target)) {
        setShowAssignedByResults(false);
        setActiveSearchField(null);
      }
      if (actionByRef.current && !actionByRef.current.contains(event.target)) {
        setShowActionByResults(false);
        setActiveSearchField(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!searchTerms.assignedTo || searchTerms.assignedTo.length < 2) {
      setAssignedToResults([]);
      return;
    }

    const timer = setTimeout(() => {
      setActiveSearchField('assignedTo');
      searchUserMutation.mutate({ 
        search: searchTerms.assignedTo,
        role: null
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerms.assignedTo]);

  useEffect(() => {
    if (!searchTerms.assignedBy || searchTerms.assignedBy.length < 2) {
      setAssignedByResults([]);
      return;
    }

    const timer = setTimeout(() => {
      setActiveSearchField('assignedBy');
      searchUserMutation.mutate({ 
        search: searchTerms.assignedBy,
        role: null
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerms.assignedBy]);

  useEffect(() => {
    if (!searchTerms.actionBy || searchTerms.actionBy.length < 2) {
      setActionByResults([]);
      return;
    }

    const timer = setTimeout(() => {
      setActiveSearchField('actionBy');
      searchUserMutation.mutate({ 
        search: searchTerms.actionBy,
        role: null
      });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerms.actionBy]);

  const handleAssignedToSelect = (user) => {
    console.log("✅ Selected assigned to user:", user);
    const displayName = user.name || user.full_name || user.email || user.username || "";
    
    setValue("assignment.assigned_to", user.id, { shouldValidate: true });
    setSearchTerms(prev => ({ ...prev, assignedTo: displayName }));
    
    setShowAssignedToResults(false);
    setAssignedToResults([]);
    setActiveSearchField(null);
  };

  const handleAssignedBySelect = (user) => {
    console.log("✅ Selected assigned by user:", user);
    const displayName = user.name || user.full_name || user.email || user.username || "";
    
    setValue("assignment.assigned_by", user.id, { shouldValidate: true });
    setSearchTerms(prev => ({ ...prev, assignedBy: displayName }));
    
    setShowAssignedByResults(false);
    setAssignedByResults([]);
    setActiveSearchField(null);
  };

  const handleActionBySelect = (user) => {
    console.log("✅ Selected action by user:", user);
    const displayName = user.name || user.full_name || user.email || user.username || "";
    
    setValue("action.action_by", user.id, { shouldValidate: true });
    setSearchTerms(prev => ({ ...prev, actionBy: displayName }));
    
    setShowActionByResults(false);
    setActionByResults([]);
    setActiveSearchField(null);
  };

  const renderSearchDropdown = (results, onSelect, show, searchTerm, isLoading, fieldName) => {
    if (!show) return null;
    
    const safeResults = Array.isArray(results) ? results : [];
    
    if (isLoading && activeSearchField === fieldName) {
      return (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
          <div className="flex items-center justify-center">
            <svg className="animate-spin h-5 w-5 text-gray-400 mr-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Searching...
          </div>
        </div>
      );
    }

    if (safeResults.length === 0 && searchTerm.length >= 2 && !isLoading) {
      return (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg p-4 text-center text-gray-500">
          No users found matching "{searchTerm}"
        </div>
      );
    }

    if (safeResults.length === 0) {
      return null;
    }

    return (
      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
        {safeResults.map((user, index) => (
          <div
            key={user.id || index}
            onClick={() => onSelect(user)}
            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0 transition-colors duration-150"
          >
            <div className="font-medium text-gray-900">
              {user.name || user.full_name || user.email || user.username || "Unknown User"}
            </div>
            <div className="text-sm text-gray-500">
              {user.role || user.position || user.department || "User"} 
              {user.email && ` • ${user.email}`}
            </div>
            <div className="text-xs text-gray-400">
              ID: {user.id}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-in slide-in-from-left duration-300">
      {/* ASSIGNMENT */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ASSIGNMENT</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Assigned To with search */}
          <div className="space-y-1 relative" ref={assignedToRef}>
            <FormInput
              label="Assigned To"
              {...register("assignment.assigned_to")}
              error={errors?.assignment?.assigned_to}
              placeholder="Enter name here"
              inputRef={(e) => {
                if (e) {
                  const { ref } = register("assignment.assigned_to");
                  if (typeof ref === 'function') {
                    ref(e);
                  }
                  assignedToInputRef.current = e;
                }
              }}
              value={searchTerms.assignedTo}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerms(prev => ({ ...prev, assignedTo: newValue }));
                setValue("assignment.assigned_to", newValue, { shouldValidate: true });
                setShowAssignedToResults(true);
                if (newValue.length >= 2) {
                  setActiveSearchField('assignedTo');
                }
              }}
              onFocus={() => {
                setShowAssignedToResults(true);
                if (searchTerms.assignedTo.length >= 2) {
                  setActiveSearchField('assignedTo');
                }
              }}
            />
            {renderSearchDropdown(
              assignedToResults,
              handleAssignedToSelect,
              showAssignedToResults,
              searchTerms.assignedTo,
              searchUserMutation.isLoading,
              'assignedTo'
            )}
          </div>

          {/* Assigned By with search */}
          <div className="space-y-1 relative" ref={assignedByRef}>
            <FormInput
              label="Assigned By"
              {...register("assignment.assigned_by")}
              error={errors?.assignment?.assigned_by}
              placeholder="Enter name here"
              inputRef={(e) => {
                if (e) {
                  const { ref } = register("assignment.assigned_by");
                  if (typeof ref === 'function') {
                    ref(e);
                  }
                  assignedByInputRef.current = e;
                }
              }}
              value={searchTerms.assignedBy}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerms(prev => ({ ...prev, assignedBy: newValue }));
                setValue("assignment.assigned_by", newValue, { shouldValidate: true });
                setShowAssignedByResults(true);
                if (newValue.length >= 2) {
                  setActiveSearchField('assignedBy');
                }
              }}
              onFocus={() => {
                setShowAssignedByResults(true);
                if (searchTerms.assignedBy.length >= 2) {
                  setActiveSearchField('assignedBy');
                }
              }}
            />
            {renderSearchDropdown(
              assignedByResults,
              handleAssignedBySelect,
              showAssignedByResults,
              searchTerms.assignedBy,
              searchUserMutation.isLoading,
              'assignedBy'
            )}
          </div>
        </div>

        {/* Assignment Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Assignment Notes
          </label>
          <textarea
            {...register("assignment.remarks")}
            rows={4}
            placeholder="Enter assignment notes here"
            className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 min-h-[120px] ${
              errors?.assignment?.remarks
                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            } placeholder-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors?.assignment?.remarks && (
            <p className="text-red-500 text-xs mt-1">
              {errors.assignment.remarks.message}
            </p>
          )}
        </div>
      </div>

      <div className="border-t border-gray-200 my-6"></div>

      {/* ACTION */}
      <div className="bg-gray-50 p-6 rounded-lg border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">ACTION</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Action By with search */}
          <div className="space-y-1 relative" ref={actionByRef}>
            <FormInput
              label="Action By"
              {...register("action.action_by")}
              error={errors?.action?.action_by}
              placeholder="Enter name here"
              inputRef={(e) => {
                if (e) {
                  const { ref } = register("action.action_by");
                  if (typeof ref === 'function') {
                    ref(e);
                  }
                  actionByInputRef.current = e;
                }
              }}
              value={searchTerms.actionBy}
              onChange={(e) => {
                const newValue = e.target.value;
                setSearchTerms(prev => ({ ...prev, actionBy: newValue }));
                setValue("action.action_by", newValue, { shouldValidate: true });
                setShowActionByResults(true);
                if (newValue.length >= 2) {
                  setActiveSearchField('actionBy');
                }
              }}
              onFocus={() => {
                setShowActionByResults(true);
                if (searchTerms.actionBy.length >= 2) {
                  setActiveSearchField('actionBy');
                }
              }}
            />
            {renderSearchDropdown(
              actionByResults,
              handleActionBySelect,
              showActionByResults,
              searchTerms.actionBy,
              searchUserMutation.isLoading,
              'actionBy'
            )}
          </div>

          {/* Date of Action */}
          <div className="space-y-1">
            <label className="block text-sm font-medium text-gray-700">
              Date of Action
            </label>
            <input
              type="date"
              {...register("action.action_date")}
              className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 ${
                errors?.action?.action_date
                  ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                  : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
              } placeholder-gray-400 focus:outline-none focus:ring-1`}
            />
            {errors?.action?.action_date && (
              <p className="text-red-500 text-xs mt-1">
                {errors.action.action_date.message}
              </p>
            )}
          </div>
        </div>

        {/* Action Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Action Notes
          </label>
          <textarea
            {...register("action.action_note")}
            rows={5}
            placeholder="Enter action notes here"
            className={`mt-1 block w-full border-2 rounded-lg px-4 py-3 transition-all duration-200 min-h-[150px] ${
              errors?.action?.action_note
                ? "border-red-300 bg-red-50 focus:border-red-500 focus:ring-red-500"
                : "border-gray-200 hover:border-gray-300 focus:border-blue-500 focus:ring-blue-500"
            } placeholder-gray-400 focus:outline-none focus:ring-1`}
          />
          {errors?.action?.action_note && (
            <p className="text-red-500 text-xs mt-1">
              {errors.action.action_note.message}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AssignmentActionsTab;